import React, { useState } from 'react';
import './Settings.css';
import CustomDropdown from '../CustomDropdown';
import { API_BASE_URL } from '../../utils/api';

function Settings({ onCreateTestFromAI, selectedApplication }) {
  // Team used to be the first tab here, and the only place to invite anyone.
  // Inviting is an admin act now - it decides which applications a person can
  // open - so it lives in the Admin tab, behind a server-side admin check.
  const [activeTab, setActiveTab] = useState('profile');
  
  // AI Integration state
  const [apiKey, setApiKey] = useState(localStorage.getItem('ai_api_key') || '');
  const [apiProvider, setApiProvider] = useState(localStorage.getItem('ai_provider') || 'openai');
  const [testDescription, setTestDescription] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // My Profile state
  const [profileData, setProfileData] = useState({
    firstName: localStorage.getItem('firstName') || '',
    lastName: localStorage.getItem('lastName') || '',
    email: localStorage.getItem('email') || localStorage.getItem('username') || '',
    avatarUrl: localStorage.getItem('avatarUrl') || ''
  });


  const handleSaveApiKey = () => {
    localStorage.setItem('ai_api_key', apiKey);
    localStorage.setItem('ai_provider', apiProvider);
    setSuccess('API configuration saved successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleTestAIConnection = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key first');
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus(null);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: apiKey.trim(),
          provider: apiProvider
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const text = await response.text();
        throw new Error(`Invalid response from server: ${text.substring(0, 100)}`);
      }

      if (data.success) {
        setConnectionStatus({
          status: 'success',
          message: data.message || 'AI API connection successful',
          model: data.model || ''
        });
        setSuccess(data.message || 'AI API connection successful!');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setConnectionStatus({
          status: 'error',
          message: data.error || 'Failed to connect to AI API'
        });
        setError(data.error || 'Failed to connect to AI API');
      }
    } catch (err) {
      let errorMessage = err.message;
      
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        errorMessage = 'Cannot connect to backend server. Please ensure the server is running on port 5000.';
      } else if (errorMessage.includes('Invalid response')) {
        errorMessage = 'Server returned an invalid response. Please check the backend logs.';
      }
      
      setConnectionStatus({
        status: 'error',
        message: errorMessage
      });
      setError(errorMessage);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleGenerateTest = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your API key first');
      return;
    }

    if (!testDescription.trim()) {
      setError('Please enter a test description');
      return;
    }

    if (!targetUrl.trim()) {
      setError('Please enter a target URL');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setSuccess(null);

    if (!selectedApplication) {
      setError('Please select an application before generating a test');
      setIsGenerating(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/generate-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey,
          provider: apiProvider,
          description: testDescription,
          targetUrl: targetUrl,
          application_id: selectedApplication.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Test generated successfully! Opening test...');
        setTestDescription('');
        setTargetUrl('');
        
        if (onCreateTestFromAI && data.test) {
          setTimeout(() => {
            onCreateTestFromAI(data.test);
            setSuccess(null);
          }, 1000);
        } else {
          setTimeout(() => setSuccess(null), 3000);
        }
      } else {
        setError(data.error || 'Failed to generate test');
      }
    } catch (err) {
      setError(err.message || 'Error connecting to AI service');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveProfile = () => {
    localStorage.setItem('firstName', profileData.firstName);
    localStorage.setItem('lastName', profileData.lastName);
    localStorage.setItem('email', profileData.email);
    setSuccess('Profile updated successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const renderMyProfileContent = () => (
    <div className="settings-form">
      <h2>My Profile</h2>
      <p className="section-description">Update your personal information and preferences.</p>
      
      <div className="form-group">
        <label htmlFor="first-name">First Name</label>
        <input
          id="first-name"
          type="text"
          value={profileData.firstName}
          onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
          placeholder="Enter your first name"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="last-name">Last Name</label>
        <input
          id="last-name"
          type="text"
          value={profileData.lastName}
          onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
          placeholder="Enter your last name"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          value={profileData.email}
          onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
          placeholder="Enter your email"
          className="form-input"
        />
      </div>

      <div className="form-actions">
        <button className="btn-save" onClick={handleSaveProfile}>
          Save Changes
        </button>
      </div>
    </div>
  );

  const renderAIIntegrationContent = () => (
    <div className="settings-form">
      <h2>AI Integration</h2>
      <p className="section-description">Connect to an LLM service to generate test cases automatically from natural language descriptions.</p>

      <div className="form-group">
        <label htmlFor="ai-provider">AI Provider</label>
        <CustomDropdown
          value={apiProvider}
          onChange={(e) => setApiProvider(e.target.value)}
          options={[
            { value: 'openai', label: 'OpenAI (GPT-4/GPT-3.5)' },
            { value: 'anthropic', label: 'Anthropic (Claude)' },
            { value: 'custom', label: 'Custom API' }
          ]}
          className="form-select"
        />
      </div>

      <div className="form-group">
        <label htmlFor="api-key">
          API Key
          <span className="info-icon" title="Your API key is stored locally and never shared">
            ℹ️
          </span>
        </label>
        <div className="input-with-toggle">
          <input
            id="api-key"
            type={showApiKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter your API key"
            className="form-input"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowApiKey(!showApiKey)}
            title={showApiKey ? "Hide API key" : "Show API key"}
          >
            {showApiKey ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                <line x1="1" y1="1" x2="23" y2="23"></line>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            )}
          </button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button className="btn-save-key" onClick={handleSaveApiKey}>
            Save API Key
          </button>
          <button
            className="btn-test-connection"
            onClick={handleTestAIConnection}
            disabled={isTestingConnection || !apiKey.trim()}
          >
            {isTestingConnection ? (
              <>
                <span className="spinner"></span>
                Testing...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                Test Connection
              </>
            )}
          </button>
        </div>
      </div>

      {connectionStatus && (
        <div className={`connection-result ${connectionStatus.status}`} style={{ marginTop: '1rem' }}>
          <div className="connection-result-header">
            <span className="connection-label">AI API Connection:</span>
            <span className={`connection-status ${connectionStatus.status}`}>
              {connectionStatus.status === 'success' ? '✓' : '✗'}
            </span>
          </div>
          <div className="connection-message">
            {connectionStatus.message}
            {connectionStatus.model && ` (Model: ${connectionStatus.model})`}
          </div>
        </div>
      )}

      <div className="form-group" style={{ marginTop: '2rem' }}>
        <label htmlFor="target-url">Target URL *</label>
        <input
          id="target-url"
          type="url"
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
          placeholder="https://example.com"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label htmlFor="test-description">Test Description *</label>
        <textarea
          id="test-description"
          value={testDescription}
          onChange={(e) => setTestDescription(e.target.value)}
          placeholder="Describe the test scenario. For example: 'Test login functionality - enter username and password, click login button, verify dashboard loads'"
          className="form-textarea"
          rows="6"
        />
      </div>

      <div className="form-actions">
        <button
          className="btn-generate"
          onClick={handleGenerateTest}
          disabled={isGenerating || !apiKey.trim() || !testDescription.trim() || !targetUrl.trim()}
        >
          {isGenerating ? (
            <>
              <span className="spinner"></span>
              Generating...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              Generate Test
            </>
          )}
        </button>
      </div>

      <div className="ai-examples">
        <h3>Example Descriptions:</h3>
        <ul>
          <li>"Test user registration: fill in name, email, password fields, submit form, verify success message"</li>
          <li>"Test search functionality: enter search term, click search button, verify results display"</li>
          <li>"Test checkout process: add item to cart, proceed to checkout, fill shipping info, complete payment"</li>
        </ul>
      </div>
    </div>
  );

  const getTabIcon = (tab) => {
    switch(tab) {
      case 'profile':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z"/>
            <path d="M10 12C5.58172 12 2 15.5817 2 20H18C18 15.5817 14.4183 12 10 12Z"/>
          </svg>
        );
      case 'ai':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 2L2 7L10 12L18 7L10 2Z"/>
            <path d="M2 13L10 18L18 13"/>
            <path d="M2 10L10 15L18 10"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-sidebar">
        <div className="settings-sidebar-header">
          <h1>Settings</h1>
          <p className="settings-subtitle">Manage your platform configuration</p>
        </div>
        <nav className="settings-nav">
          <button
            className={`settings-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {getTabIcon('profile')}
            <span>My Profile</span>
          </button>
          <button
            className={`settings-nav-item ${activeTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('ai')}
          >
            {getTabIcon('ai')}
            <span>AI Integration</span>
          </button>
        </nav>
      </div>
      <div className="settings-main">
        {success && (
          <div className="message-success">
            {success}
          </div>
        )}
        {error && (
          <div className="message-error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {activeTab === 'profile' && renderMyProfileContent()}
        {activeTab === 'ai' && renderAIIntegrationContent()}
      </div>
    </div>
  );
}

export default Settings;
